import { DomainBaseComponent, wrapBusy } from '@/shared/domain-base.component';
import { UtilFns } from '@/shared/util-fns';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { QBAccount } from 'app/model/entities/qb-account';
import { QBItem } from 'app/model/entities/qb-item';
import { DomainService } from 'app/shared/domain.service';
import { environment } from 'environments/environment';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { firstValueFrom } from 'rxjs';
import { AccountsAndItems, CompanyInfo, IntuitAccountTypeEnum, IntuitItemTypeEnum } from './qb-interfaces';
import {
  GL_POSTING_MAP,
  PostingGroup,
  PostingTransaction,
  getTransactionsUsingAccount,
  getTransactionsUsingItem,
  GL_MAPPING_CATEGORIES,
  GL_MAPPING_DESCRIPTORS,
  GlAccountingCategory,
  GlMappingDescriptor,
  GlMappingSource,
  getDescriptorsForCategory,
  isVisibleRole,
} from './gl-posting-map';

@Component({
  templateUrl: './qb-config.component.html',
  styleUrls: ['./qb-config.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class QbConfigComponent extends DomainBaseComponent implements OnInit {
  qbAccounts!: QBAccount[];
  qbItems!: QBItem[];
  accountsAndItems!: AccountsAndItems;
  companyInfo!: CompanyInfo;
  message?: string;
  showAllAccounts = false;
  showAllItems = false;

  constructor(
    domainService: DomainService,
    private httpClient: HttpClient,
  ) {
    super(domainService);
  }

  async ngOnInit() {
    if (this.uow.hasChanges()) {
      this.dialogService.showOkMessage('Unable to open', 'Please save or cancel before trying this.');
      return;
    }

    await this.loadData();
    this.isPageReady = true;
  }

  @wrapBusy
  private async loadData() {
    const accountsUrl = environment.apiRoot + 'api/quickbooks/QueryAccountsAndItems';
    const companyInfoUrl = environment.apiRoot + 'api/quickbooks/QueryCompanyInfo';
    try {
      [this.qbAccounts, this.qbItems, this.accountsAndItems, this.companyInfo] = await Promise.all([
        this.dbQueryService.getQBAccounts(),
        this.dbQueryService.getQBItems(),
        firstValueFrom(this.httpClient.get<AccountsAndItems>(accountsUrl)),
        firstValueFrom(this.httpClient.get<CompanyInfo>(companyInfoUrl)),
      ]);
    } catch (err) {
      console.error(err);
      const msg = UtilFns.getErrorMessage(err);
      if (msg && msg.includes('Unable to reach accounting system')) {
        this.message = 'Unable to reach accounting system.  Please reset the Quickbooks API Token.';
      } else {
        this.message = 'Unknown error from server: ' + msg;
      }
    }
  }

  getAccountsByType(type: string) {
    if (this.showAllAccounts) {
      return this.accountsAndItems.accounts;
    }
    const types = type.split('|');
    const typenums = types.map((x) => IntuitAccountTypeEnum[x]);
    return this.accountsAndItems.accounts.filter((x) => types.includes(x.AccountType) || typenums.includes(x.AccountType));
  }

  getItemsByType(type: string) {
    if (this.showAllItems) {
      return this.accountsAndItems.items;
    }
    const types = type.split('|');
    const typenums = types.map((x) => IntuitItemTypeEnum[x]);
    return this.accountsAndItems.items.filter((x) => types.includes(x.Type) || typenums.includes(x.Type));
  }

  // /** Get all types that have any items associated with them */
  // getItemTypes() {
  //   let types = Object.keys(IntuitItemTypeEnum).filter(key => !isNaN(Number(key)));
  //   const items = this.accountsAndItems.items;
  //   types = types.filter(x => items.some(i => i.Type == x));
  //   return types.map(x => IntuitItemTypeEnum[x]);
  // }

  onAccountChange(evt: DropdownChangeEvent, acct: QBAccount) {
    const id = evt.value;
    const selected = this.accountsAndItems.accounts.find((x) => x.Id == id);
    if (selected) {
      acct.qbAccountId = id;
      acct.qbAccountName = selected.Name;
    }
  }

  onItemChange(evt: DropdownChangeEvent, item: QBItem) {
    const id = evt.value;
    const selected = this.accountsAndItems.items.find((x) => x.Id == id);
    if (selected) {
      item.qbItemId = id;
      item.qbItemName = selected.Name;
    }
  }

  async save() {
    await this.dbSaveService.saveChanges();
  }

  async undo() {
    this.qbAccounts.slice().forEach((x) => x.entityAspect.rejectChanges());
    this.qbItems.slice().forEach((x) => x.entityAspect.rejectChanges());
    await this.loadData();
  }

  async close() {
    if (this.uow.hasChanges()) {
      const ok = await this.dialogService.yesNoDialog('Changes have not been saved', 'Do you want to save before exiting?');
      if (ok) {
        await this.dbSaveService.saveChanges();
      } else {
        this.dbSaveService.rejectChanges();
      }
    }
  }

  getNewQbToken() {
    const url = environment.apiRoot + 'api/Quickbooks/IntuitLogin';
    window.open(url, '_blank');
  }

  // ============================================================
  // GL Posting Map (Scheme E) — methods supporting the new transaction-grouped
  // panels. The posting-map data lives in gl-posting-map.ts; this component
  // just renders it and cross-references to the configured QBAccount/QBItem
  // rows already loaded on this screen.
  // ============================================================

  postingGroups: PostingGroup[] = GL_POSTING_MAP;

  /** Find the configured QBAccount row for a given role name (e.g. 'Cash').
   *  Returns null if not configured — surfaced as 'unmapped' in the UI. */
  getAccountForRole(role: string): QBAccount | null {
    return this.qbAccounts?.find((a) => a.name === role) ?? null;
  }

  /** Find the configured QBItem row for a given role name (e.g. 'Product'). */
  getItemForRole(role: string): QBItem | null {
    return this.qbItems?.find((i) => i.name === role) ?? null;
  }

  /** Resolved QB account name. Reads LIVE from the QB account catalog by id,
   *  so the value tracks the dropdown in real time even when the user has
   *  changed the selection but not yet saved. Falls back to the persisted
   *  qbAccountName if the live lookup misses (e.g. the QB account was deleted
   *  on the QB side since the Uniformax row was saved). */
  getResolvedAccountName(role: string): string {
    const acct = this.getAccountForRole(role);
    if (!acct) return '(unmapped)';
    const live = this.accountsAndItems?.accounts?.find((x) => x.Id == acct.qbAccountId);
    return live?.Name ?? acct.qbAccountName ?? '(unmapped)';
  }

  /** Resolved QB item name. Same live-read pattern as getResolvedAccountName. */
  getResolvedItemName(role: string): string {
    const item = this.getItemForRole(role);
    if (!item) return '(unmapped)';
    const live = this.accountsAndItems?.items?.find((x) => x.Id == item.qbItemId);
    return live?.Name ?? item.qbItemName ?? '(unmapped)';
  }

  /** Cross-reference shown under each role-mapping row — "Used by these transactions:" */
  getAccountUsageSummary(role: string): string {
    const txns = getTransactionsUsingAccount(role);
    if (txns.length === 0) return 'Not currently referenced by any transaction.';
    return txns.map((t) => t.displayName).join(' · ');
  }

  getItemUsageSummary(role: string): string {
    const txns = getTransactionsUsingItem(role);
    if (txns.length === 0) return 'Not currently referenced by any transaction.';
    return txns.map((t) => t.displayName).join(' · ');
  }

  // ============================================================
  // General Ledger Posting Configuration — accounting-category-driven
  // render on the left panel. Replaces the prior two-section split
  // (Account Mapping + Invoice & Credit-Memo Line Items) with a single
  // section organized by accounting classification.
  // ============================================================

  /** All categories in display order. */
  readonly glCategoriesInOrder: GlAccountingCategory[] = ['BalanceSheet', 'Revenue', 'ContraRevenue'];

  glCategoryMeta(cat: GlAccountingCategory) {
    return GL_MAPPING_CATEGORIES[cat];
  }

  glDescriptorsFor(cat: GlAccountingCategory): GlMappingDescriptor[] {
    return getDescriptorsForCategory(cat);
  }

  /** Resolve a descriptor to its currently configured QBAccount or QBItem entity.
   *  Returns the live row so the dropdown remains bound to it. */
  qbAccountFor(descriptor: GlMappingDescriptor): QBAccount | null {
    if (descriptor.source !== 'QBAccount') return null;
    return this.qbAccounts?.find((a) => a.name === descriptor.role) ?? null;
  }

  qbItemFor(descriptor: GlMappingDescriptor): QBItem | null {
    if (descriptor.source !== 'QBItem') return null;
    return this.qbItems?.find((i) => i.name === descriptor.role) ?? null;
  }

  /** Used by the per-descriptor 'Used by:' line. */
  usageSummaryFor(descriptor: GlMappingDescriptor): string {
    return descriptor.source === 'QBAccount' ? this.getAccountUsageSummary(descriptor.role) : this.getItemUsageSummary(descriptor.role);
  }

  /** Visibility predicate (referenced from a checkbox-toggled hidden-rows view if we add one later). */
  isVisibleRole = isVisibleRole;

  /** Map a role + source to the accounting-friendly display label used on the
   *  left side (e.g. role='Tax' source='QBItem' → 'Sales Tax'). Falls back to
   *  the raw role name if the descriptor isn't found (e.g. a role that's still
   *  referenced by a transaction but has been removed from the visible-roles
   *  list — which would itself be a config inconsistency worth surfacing). */
  getDisplayLabelForRole(role: string | null, kind: 'account' | 'item' | 'qbAuto'): string {
    if (!role || kind === 'qbAuto') return '';
    const source: GlMappingSource = kind === 'account' ? 'QBAccount' : 'QBItem';
    const d = GL_MAPPING_DESCRIPTORS.find((d) => d.source === source && d.role === role);
    return d?.displayLabel ?? role;
  }
}
